import { CSSProperties, FC, JSX, useEffect, useMemo, useState } from "react";
import { iconSvgAttributes } from "./iconSvgAttributes";
import { iconLookup } from "./iconLookup";
import { sharedLineIconProps } from "./interfaces";

export interface IconProps extends React.SVGAttributes<SVGElement> {
  /**
   * Classnames to be added to the parent <svg> element
   */
  classNames?: string;
  /**
   * Any particular fill color for the entire icon.
   * This can also be applied as "fill" in scss, or "color" in scss.
   * The fill defaults to "currentColor", or whatever the text around it is.
   */
  color?: string;
  /**
   * A text description for the icon. If the icon is not decorative,
   * this will be placed in the icon's <title> child. It's like an
   * image's alt text.
   * This should describe the action of the icon, if applicable, instead of 
   * the appearance of the icon.
   */
  description?: string;
  /**
   * The desired height of the icon. Icons will maintain their internal aspect ratios.
   */
  height?: string;
  /**
   * The name of the icon, from this handy enum.
   */
  iconName: string;
  /**
   * A decorative icon - one that doesn't convey any meaning on its own,
   * or lives alongside text that conveys its meaning - will be hidden 
   * from screen readers and will not have an internal text description.
   */
  isDecorative?: boolean;
  /**
   * CSS properties to apply to the <path> element inside the svg
   */
  pathStyle?: CSSProperties;
  /**
   * HTML Attributes that will be spread onto the svg wrapper
   */
  svgAttributes?: React.SVGAttributes<SVGElement>;
  /**
   * CSS properties to apply ot the parent <svg>
   */
  svgStyle?: CSSProperties;
  /**
   * Hopefully-unique id used to differentiate it and its ids 
   * from others on the page.
   * Leaving this optional because icons, on their own, should not be interactive.
   */
  testId?: string;
  /**
   * The desired width of the icon. Icons will maintain their internal aspect ratios.
   */
  width?: string;
};

export const QesIcon: FC<IconProps> = (props: IconProps) => {
  const sharedSvgAttributes = useMemo(() => {
    const classNames = "qes-icon " + (props.classNames || "");
    return iconSvgAttributes(props.testId, classNames, props.svgStyle, props.isDecorative);
  }, [props.color, props.testId, props.classNames, props.svgStyle, props.isDecorative]);

  const { svgCode, svgProps = { height: "24", width: "24" } } = iconLookup.get(props.iconName);
  const [calculatedHeight, setCalculatedHeight] = useState<string>();
  const [calculatedWidth, setCalculatedWidth] = useState<string>();

  const svgBody = useMemo(() => {
    return svgCode(props.color, props.pathStyle);
  }, [svgCode, props.testId, props.color, props.pathStyle]);

  /** 
   * Adjust the dimensions as necessary depending on what was provided.
   * The general formula here is (height1 / width1) = (height2 / width2).
   * 1. If there's a width and no height, just calculate a height according to its aspect ratio.
   * 2. If there's a height and no width, just calculate a width according to its aspect ratio.
   * 3. If there's neither, determine whether height or width is larger. Set that dimension to 1em (current font size),
   *    and calculate a value for the other dimension according to its aspect ratio.
   * 4. If both are provided, just use those. This mostly applies when dimensions are added dynamically and the 
   *    calculated values need to be removed, like in storybook.
   */
  useEffect(() => {
    if (!!props.width && !props.height) {
      setCalculatedHeight(((parseFloat(svgProps.height) / parseFloat(svgProps.width)) * parseFloat(props.width)).toString());
    } else if (!props.width && !!props.height) {
      setCalculatedWidth((parseFloat(props.height) / (parseFloat(svgProps.height) / parseFloat(svgProps.width))).toString())
    } else if (!props.width && !props.height) {
      if (parseFloat(svgProps.height) > parseFloat(svgProps.width)) {
        const newHeight = 1.1; // default 1em;
        const newCalculatedWidth = newHeight / (parseFloat(svgProps.height) / parseFloat(svgProps.width));
        setCalculatedWidth(`${newCalculatedWidth}em`);
        setCalculatedHeight(`${newHeight}em`);
      } else {
        const newWidth = 1.1;
        const newCalculatedHeight = ((parseFloat(svgProps.height) / parseFloat(svgProps.width)) * newWidth);
        setCalculatedHeight(`${newCalculatedHeight}em`);
        setCalculatedWidth(`${newWidth}em`);
      }
    } else if (!!props.width && !!props.height) {
      setCalculatedHeight(undefined);
      setCalculatedWidth(undefined);
    }
  }, [props.width, props.height, svgProps]);

  if (svgBody.hasOwnProperty("length")) {
    return (
      <svg
        width={calculatedWidth || props.width || "1em"}
        height={calculatedHeight || props.height || "1em"}
        viewBox={`0 0 ${svgProps.width} ${svgProps.height}`}
        {...sharedSvgAttributes}
        {...(props.svgAttributes || {})}
      >
        {!props.isDecorative && !!props.description && <title>{props.description}</title>}
        {(svgBody as string[]).map((d, index) => (
          <path
            key={index}
            d={d}
            {...sharedLineIconProps(props.color, props.pathStyle)}
          />
        ))}
      </svg>
    )
  }

  return (
    <svg
      width={calculatedWidth || props.width || "1em"}
      height={calculatedHeight || props.height || "1em"}
      viewBox={`0 0 ${svgProps.width} ${svgProps.height}`}
      {...sharedSvgAttributes}
      {...(props.svgAttributes || {})}
    >
      {!props.isDecorative && !!props.description && <title>{props.description}</title>}
      {svgBody as JSX.Element}
    </svg>
  );
}
